import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * LoginPage component for Wildlife Migration Tracker Admin Dashboard
 * Professional dark theme login form with validation and error handling
 */
function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 300));

    const result = login(username, password);

    if (result.success) {
      navigate('/admin/dashboard', { replace: true });
    } else {
      setError(result.error || 'Login failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo/Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-primary-container rounded-2xl shadow-[0px_12px_32px_rgba(0,0,0,0.4)] mb-4">
            <span className="material-symbols-outlined text-4xl text-on-primary">admin_panel_settings</span>
          </div>
          <h1 className="text-3xl font-headline text-on-surface mb-2">
            Admin Dashboard
          </h1>
          <p className="text-slate-400 font-body">
            Migration Journal
          </p>
        </div>

        {/* Login Form Card */}
        <div className="bg-surface-container-high/60 backdrop-blur-xl rounded-3xl shadow-[0px_12px_32px_rgba(0,0,0,0.4)] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-error-container/20 rounded-xl p-4 flex items-start gap-3">
                <span className="material-symbols-outlined text-error text-lg">error</span>
                <p className="text-error text-sm font-body">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-on-surface-variant mb-2 font-label">
                Username
              </label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-xl">person</span>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-container-lowest text-on-surface placeholder-slate-500 rounded-xl focus:ring-1 focus:ring-primary/40 transition-all font-body"
                  placeholder="Enter your username"
                  autoComplete="username"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-on-surface-variant mb-2 font-label">
                Password
              </label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-xl">lock</span>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-container-lowest text-on-surface placeholder-slate-500 rounded-xl focus:ring-1 focus:ring-primary/40 transition-all font-body"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-gradient-to-r from-primary to-primary-container hover:from-primary-fixed-dim hover:to-primary disabled:from-surface-container-high disabled:to-surface-container text-on-primary font-semibold rounded-xl shadow-[0px_12px_32px_rgba(0,0,0,0.3)] transition-all duration-200 flex items-center justify-center gap-2 font-label"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <span className="material-symbols-outlined text-xl">arrow_forward</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6">
            <p className="text-center text-slate-500 text-sm font-body">
              Protected area. Authorized personnel only.
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-slate-400 hover:text-primary text-sm transition-colors inline-flex items-center gap-2 font-body">
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            Back to Migration Journal
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
